/**
 * The dive whine — **a declared port invention** (user, 2026-08-26), not a
 * recovered sound.
 *
 * Red Baron plays *nothing* off airspeed except the `SIMSNDS-25` wind bed this
 * port already rides (`continuous-params.ts`'s `windGain`). The four captured
 * `RB.EXE` flights in `docs/formats/sound-music.md` contain no cue that keys on,
 * sweeps or changes level in a dive; there is no whine in the original to be
 * faithful to. The user asked for one anyway, and it lands where every other
 * "the original did not do this" affordance in the port lands: under **ENHANCED
 * EFFECTS** (`flight/effects-settings.ts`), silent the moment that switch is
 * off, and written down as an invention rather than smuggled in as a
 * restoration.
 *
 * ## What it models
 *
 * A biplane in a dive sings because its exposed round members shed vortices, and
 * the shedding frequency is the Strouhal relation
 *
 * ```
 *   f = St · V / d          St ≈ 0.2 for a cylinder in the subcritical range
 * ```
 *
 * — pitch **proportional to airspeed**, which is the one behaviour that makes a
 * dive whine read as a dive rather than as a siren cue. Two members of a WWI
 * scout matter, and they are nearly two decades apart in diameter, so the
 * airframe sings in two bands at once rather than one:
 *
 * - the **interplane and cabane struts**, ~25 mm across their streamline
 *   section — `f = 8·V`, a low moan (440 Hz at 55 m/s, 840 Hz at 105 m/s);
 * - the **flying and landing wires**, ⅛-inch (3.2 mm) round cable — `f = 62.5·V`,
 *   the actual whistle (3.4 kHz at 55 m/s, 6.6 kHz at 105 m/s).
 *
 * Both are rendered as **band-passed noise**, not oscillators: vortex shedding
 * is a narrow-band random process, and a pure tone is exactly the Hollywood
 * siren the user asked this not to be. A `BiquadFilterNode` in `bandpass` mode is
 * the same DSP an `AudioWorklet` would have had to reimplement by hand, so this
 * layer runs on native nodes hung off the same `engineGain` bus the wind, fire
 * and stall-rush loops use — the parameters, which is the part worth testing,
 * live in the pure functions below exactly the way `continuous-params.ts` holds
 * the wind bed's.
 *
 * ## What is invented on top of the physics
 *
 * The Strouhal law fixes the *pitch*. Nothing fixes the *loudness*, so the level
 * curve is a port-side choice, stated here so nobody mistakes it for a
 * measurement:
 *
 * - the band runs from `DIVE_WHINE_FLOOR_MPS` (above every scout's cruise) to
 *   `DIVE_WHINE_CEILING_MPS` (past the dive the flight model calibrates to), and
 * - the response is **cubed**, not squared like the wind bed, because the brief
 *   is "inaudible at cruise, prominent in a dive" and a square still leaves 4 %
 *   of full level a quarter of the way up the band where a cube leaves 1.6 %.
 *
 * The wires' `Q` also rises with speed (`wireBandQ`) so the whistle *emerges*
 * from the rush rather than merely getting louder — shedding does grow more
 * coherent with Reynolds number, but the numbers picked are taste, not a
 * measurement.
 *
 * ## Determinism
 *
 * The noise is a seeded `xorshift32` table (`whineNoise`), not `Math.random`, so
 * two runs of the same flight render the same samples. Nothing here reads or
 * writes the sim: `updateDiveWhine` takes one airspeed and one boolean.
 */

import { clamp01, rampClamped } from "./continuous-params.ts";

// --- The speed band. One pair of numbers, shared by the pitch law and the level law below.

/**
 * Bottom of the audible band, chosen off `flight-tunables.md` §8.1's own
 * measured numbers rather than picked: the fastest airframe anyone has timed in
 * the original tops out **level** at 115.5 mph = **51.6 m/s** (SPAD XIII, ROC 0,
 * ~8,070 ft), and the Camel at 99.7 mph = 44.6 m/s. Hands-off trimmed cruise is
 * 42.8 m/s (Camel) and 50.0 m/s (SPAD). Putting the floor at 55 m/s therefore
 * means **no aeroplane in the game reaches the whine in level flight at all** —
 * the only way to hear it is to point the nose down, which is the whole brief.
 */
export const DIVE_WHINE_FLOOR_MPS = 55;

/**
 * Top of the band. §8.1's measured dive — pitch −39.1°, 109 → 226 mph over 17 s,
 * asymptote ≈ 230 mph — is **101-103 m/s**, and `flight-calibration.test.ts`
 * pins the port's own steep dive against it. 105 puts the ceiling just past
 * terminal, so even the hardest dive the flight model allows lands near, not
 * flat against, full level.
 */
export const DIVE_WHINE_CEILING_MPS = 105;

// --- The pitch law.

/** Strouhal number for a circular cylinder in the subcritical Reynolds range — the textbook 0.2. */
export const STROUHAL_NUMBER = 0.2;

/** Streamline interplane/cabane strut, across the section. */
export const STRUT_THICKNESS_M = 0.025;

/** ⅛-inch round bracing cable — the common WWI flying/landing wire size. */
export const WIRE_DIAMETER_M = 0.0032;

/**
 * Airspeed the pitch law is evaluated at, clamped at both ends.
 *
 * The low clamp is the load-bearing one: `f = St·V/d` goes to 0 Hz on a parked
 * aeroplane, and a bandpass parked at 0 Hz is not a filter, it is a mute with a
 * transient waiting in it. Pinning the low end at the floor speed means the
 * filters sit exactly where the whine first becomes audible whatever the
 * aeroplane is doing, so switching ENHANCED EFFECTS on mid-flight never sweeps a
 * filter across the spectrum to catch up.
 *
 * The high clamp only bites past a dive that is already tearing the wings off
 * (`DIVE_WHINE_PITCH_CEILING_MPS`); it is there so an unbounded speed — a bad
 * tick, a debug teleport — cannot walk the wire band into the ultrasonic and
 * silently mute the layer.
 */
export const DIVE_WHINE_PITCH_CEILING_MPS = 140;

export function whinePitchSpeed(airspeedMps: number): number {
  if (!Number.isFinite(airspeedMps)) return DIVE_WHINE_FLOOR_MPS;
  return Math.min(DIVE_WHINE_PITCH_CEILING_MPS, Math.max(DIVE_WHINE_FLOOR_MPS, airspeedMps));
}

/** Shedding frequency for a member of diameter `diameterM` at `airspeedMps`, clamped by `whinePitchSpeed`. */
export function sheddingHz(airspeedMps: number, diameterM: number): number {
  return (STROUHAL_NUMBER * whinePitchSpeed(airspeedMps)) / diameterM;
}

export function strutBandHz(airspeedMps: number): number {
  return sheddingHz(airspeedMps, STRUT_THICKNESS_M);
}

export function wireBandHz(airspeedMps: number): number {
  return sheddingHz(airspeedMps, WIRE_DIAMETER_M);
}

// --- The level law (INVENTED — see the header).

/**
 * Squared — the same shape the wind bed's own retune settled on
 * (`continuous-params.ts`'s `windGain`), deliberately.
 *
 * An earlier draft cubed it to buy "inaudible at cruise", then the measurement
 * above made that argument redundant: with the floor at 55 m/s the whine is not
 * quiet at cruise, it is **identically zero**, because no aeroplane in the game
 * flies level that fast. All the exponent controls is how the layer fills the
 * band it does own, and a cube spent the first two thirds of a dive at under
 * −13 dB of its own peak — the effect arriving all at once near terminal rather
 * than rising with the aeroplane. Squared keeps this layer and the wind bed
 * answering the same question with the same curve; where they differ is the
 * band, which is the whole point.
 */
export const DIVE_WHINE_RESPONSE_EXPONENT = 2;

/** 0 at or below the floor, 1 at or above the ceiling, squared in between. */
export function diveWhineIntensity(airspeedMps: number): number {
  const t = clamp01(rampClamped(airspeedMps, DIVE_WHINE_FLOOR_MPS, DIVE_WHINE_CEILING_MPS, 0, 1));
  return t ** DIVE_WHINE_RESPONSE_EXPONENT;
}

/**
 * Peak gain of each band at `DIVE_WHINE_CEILING_MPS` and above.
 *
 * **MEASURED** (2026-08-26, `OfflineAudioContext` render of this graph beside
 * the real wind bed and the real OPL2 engine worklet at 48 kHz) rather than
 * picked, which is the rule `ENGINE_SYNTH_LEVEL` was made to carry: a level
 * chosen by feel is a level nobody can re-derive.
 *
 * **The comparison had to be per-band, and that is the finding.** Full-band RMS
 * says nothing useful here — the engine voice is a constant −17 dBFS RMS and the
 * wind bed at terminal is −27.9, so any narrow layer measured across the whole
 * spectrum reads as "inaudible" whatever it actually sounds like. Measured
 * instead inside each whine band's own ±(0.8…1.25)×f window (roughly a critical
 * band at these frequencies), at 103 m/s, before this calibration:
 *
 * ```
 * layer                    fullRMS   strut band   wire band
 *                                   (659-1030)   (5150-8047)
 * wind bed                   -27.9       -44.3       -46.1
 * engine voice (OPL2)        -17.0       -38.1       -33.3
 * ```
 *
 * So each band gets its own reference, and they are different references on
 * purpose:
 *
 * - **struts** are aimed at the **wind bed's** own level in that band. The moan
 *   is meant to thicken the rush, not to arrive as a second voice, and matching
 *   the bed there adds ~3 dB of low-mid to a sound the player already knows.
 * - **wires** are aimed **6 dB under the engine voice's** level in that band —
 *   which is still ~7 dB *over* the wind bed up there, so the whistle is
 *   unmistakably a new sound while staying under the loudest thing in the mix.
 *   6 dB rather than parity because the wire band is narrow and the engine's
 *   content there is spread harmonics; equal in-band energy would not read as
 *   equal loudness.
 *
 * The two derivations are independent and landed within 5 % of each other, which
 * is why the constants look almost equal. See `docs/engineering/audio-plan.md`
 * §7 for the achieved figures.
 */
export const STRUT_BAND_PEAK_GAIN = 0.11;
export const WIRE_BAND_PEAK_GAIN = 0.105;

/** Broad — the struts moan rather than sing, and a narrow strut band would just sound like a second wire. */
export const STRUT_BAND_Q = 3;

/** The wires' `Q`, floor speed → ceiling speed. Rising, so the whistle emerges from the rush instead of only getting louder. */
export const WIRE_BAND_Q_MIN = 5;
export const WIRE_BAND_Q_MAX = 12;

export function wireBandQ(airspeedMps: number): number {
  return rampClamped(airspeedMps, DIVE_WHINE_FLOOR_MPS, DIVE_WHINE_CEILING_MPS, WIRE_BAND_Q_MIN, WIRE_BAND_Q_MAX);
}

/** One band's three numbers for a frame. */
export interface WhineBand {
  readonly hz: number;
  readonly q: number;
  readonly gain: number;
}

export interface DiveWhineFrame {
  readonly strut: WhineBand;
  readonly wire: WhineBand;
}

/**
 * Everything the graph needs for one frame.
 *
 * `enabled` is ENHANCED EFFECTS. **Off means gain 0 on both bands and nothing
 * else** — the filters keep tracking airspeed, so switching the row back on
 * fades a whine that is already at the right pitch in, instead of sweeping two
 * filters up from wherever they were parked.
 */
export function diveWhineFrame(airspeedMps: number, enabled: boolean): DiveWhineFrame {
  const intensity = enabled ? diveWhineIntensity(airspeedMps) : 0;
  return {
    strut: { hz: strutBandHz(airspeedMps), q: STRUT_BAND_Q, gain: STRUT_BAND_PEAK_GAIN * intensity },
    wire: { hz: wireBandHz(airspeedMps), q: wireBandQ(airspeedMps), gain: WIRE_BAND_PEAK_GAIN * intensity },
  };
}

// --- The noise table.

/** Loop length. Long enough that the table's own period is not a pitch, short enough to build in a frame. */
export const DIVE_WHINE_NOISE_SECONDS = 4;

/** Fixed seed — the whole point of not calling `Math.random` here (see the header's determinism note). */
export const DIVE_WHINE_NOISE_SEED = 0x5eed1a71;

/**
 * `sampleCount` samples of uniform white noise in [-1, 1), from a seeded
 * `xorshift32`.
 *
 * No crossfade at the loop seam, deliberately: adjacent samples of white noise
 * are already uncorrelated, so the wrap from the last sample to the first is
 * statistically indistinguishable from any other pair and cannot click. What a
 * short noise loop *can* do is read as a periodic chuff — hence
 * `DIVE_WHINE_NOISE_SECONDS`, not 0.2.
 */
export function whineNoise(sampleCount: number, seed: number = DIVE_WHINE_NOISE_SEED): Float32Array<ArrayBuffer> {
  const out = new Float32Array(Math.max(0, Math.floor(sampleCount)));
  // A zero state is xorshift's one fixed point — it would emit silence for ever.
  let state = (seed | 0) === 0 ? 1 : seed | 0;
  for (let i = 0; i < out.length; i++) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    // `>>> 0` reads the 32 bits as unsigned so the map to [-1, 1) is uniform rather than folded around zero.
    out[i] = ((state >>> 0) / 0x8000_0000) - 1;
  }
  return out;
}

// --- The graph. Native nodes on the caller's bus; see the header for why not a worklet.

export interface DiveWhineHandle {
  readonly source: AudioBufferSourceNode;
  readonly strutFilter: BiquadFilterNode;
  readonly wireFilter: BiquadFilterNode;
  readonly strutGain: GainNode;
  readonly wireGain: GainNode;
}

/**
 * How fast a parameter chases its target, as `setTargetAtTime`'s time constant.
 *
 * Every number this layer writes is written through `setTargetAtTime` rather
 * than `.value`, which is the difference between "no steps or clicks" and
 * hoping: assigning a filter frequency 60 times a second steps it 60 times a
 * second, and a stepped cutoff on a resonant band is a zipper. 60 ms reaches
 * ~63 % of a new target in one time constant and is well under the time a dive
 * takes to change speed, so the whine still tracks the aeroplane.
 */
export const DIVE_WHINE_SMOOTHING_S = 0.06;

function chase(param: AudioParam, target: number, now: number): void {
  param.setTargetAtTime(target, now, DIVE_WHINE_SMOOTHING_S);
}

/**
 * Builds the two-band circuit on `bus` and starts the noise loop immediately.
 * Both band gains start at 0, so it contributes nothing until `updateDiveWhine`
 * is called with an airspeed above the floor and ENHANCED EFFECTS on.
 */
export function createDiveWhine(context: AudioContext, bus: GainNode): DiveWhineHandle {
  const buffer = context.createBuffer(1, Math.round(context.sampleRate * DIVE_WHINE_NOISE_SECONDS), context.sampleRate);
  buffer.copyToChannel(whineNoise(buffer.length), 0);
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  const build = (type: "bandpass", hz: number, q: number): { filter: BiquadFilterNode; gain: GainNode } => {
    const filter = context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = hz;
    filter.Q.value = q;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    return { filter, gain };
  };

  const strut = build("bandpass", strutBandHz(DIVE_WHINE_FLOOR_MPS), STRUT_BAND_Q);
  const wire = build("bandpass", wireBandHz(DIVE_WHINE_FLOOR_MPS), WIRE_BAND_Q_MIN);
  source.start();
  return { source, strutFilter: strut.filter, wireFilter: wire.filter, strutGain: strut.gain, wireGain: wire.gain };
}

/** Writes one frame's six numbers, all smoothed. `now` is `AudioContext.currentTime`. */
export function updateDiveWhine(handle: DiveWhineHandle, airspeedMps: number, enabled: boolean, now: number): void {
  const frame = diveWhineFrame(airspeedMps, enabled);
  chase(handle.strutFilter.frequency, frame.strut.hz, now);
  chase(handle.strutFilter.Q, frame.strut.q, now);
  chase(handle.strutGain.gain, frame.strut.gain, now);
  chase(handle.wireFilter.frequency, frame.wire.hz, now);
  chase(handle.wireFilter.Q, frame.wire.q, now);
  chase(handle.wireGain.gain, frame.wire.gain, now);
}

export function disposeDiveWhine(handle: DiveWhineHandle): void {
  handle.source.stop();
  handle.source.disconnect();
  handle.strutGain.disconnect();
  handle.wireGain.disconnect();
}
